import {Box, FormHelperText, Stack, Typography, CircularProgress} from '@mui/material';
import {styled} from '@mui/material/styles';
import React from 'react';
import {useDropzone} from 'react-dropzone';
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    PathValue,
    RegisterOptions,
    ValidationRule,
} from 'react-hook-form';

// project-imports
import {DropzoneType} from 'configs/themeConfig';
import PlaceholderContent from './PlaceholderContent';

// types
import {CustomFile} from 'types';

const DropzoneWrapper = styled('div')(({theme}) => ({
    outline: 'none',
    padding: theme.spacing(5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: '1px dashed',
    borderColor: theme.palette.secondary.main,
    '&:hover': {opacity: 0.72, cursor: 'pointer'},
}));

// Document file type presets
export const DOCUMENT_ACCEPT_TYPES = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
};

export const IMAGE_ACCEPT_TYPES = {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp', '.svg'],
};

export const VIDEO_ACCEPT_TYPES = {
    'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v'],
};

export const ALL_MEDIA_ACCEPT_TYPES = {
    ...IMAGE_ACCEPT_TYPES,
    ...DOCUMENT_ACCEPT_TYPES,
    ...VIDEO_ACCEPT_TYPES,
};

export type AppControlledMediaFileUploadProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
    errorMessage?: string;
    required?: string | ValidationRule<boolean>;
    label?: string | React.ReactNode;
    defaultValue?: CustomFile[] | null;
    showList?: boolean;
    type?: DropzoneType;
    previewImageSize?: number;
    multiple?: boolean;
    accept?: Record<string, string[]>;
    maxFiles?: number;
    maxSize?: number;
    sx?: any;
    /** Helper prop to quickly set accept types for documents only */
    documentsOnly?: boolean;
    /** Helper prop to quickly set accept types for images only */
    imagesOnly?: boolean;
    /** Helper prop to quickly set accept types for videos only */
    videosOnly?: boolean;
    /** Helper prop to quickly set accept types for both images and documents */
    allowAllMedia?: boolean;
    loading?: boolean;
};

export const AppControlledMediaFileUpload = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    rules,
    defaultValue = null as PathValue<TFieldValues, TName> & (CustomFile[] | null),
    required,
    errorMessage,
    label,
    showList = true, // Default to true for better preview experience
    type = DropzoneType.DEFAULT,
    previewImageSize = 80,
    multiple = true,
    accept,
    maxFiles,
    maxSize,
    sx,
    documentsOnly = false,
    imagesOnly = false,
    videosOnly = false,
    allowAllMedia = false,
    loading = false,
    ...rest
}: AppControlledMediaFileUploadProps<TFieldValues, TName>) => {
    const customRules: Omit<
        RegisterOptions<TFieldValues, TName>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    > = rules
        ? rules
        : {
              required: required ? `${label || 'Files'} is required` : false,
          };

    // Determine accept types based on helper props
    const getAcceptTypes = (): Record<string, string[]> => {
        if (accept) return accept;
        if (documentsOnly) return DOCUMENT_ACCEPT_TYPES;
        if (imagesOnly) return IMAGE_ACCEPT_TYPES;
        if (videosOnly) return VIDEO_ACCEPT_TYPES;
        if (allowAllMedia) return ALL_MEDIA_ACCEPT_TYPES;
        return IMAGE_ACCEPT_TYPES; // Default to images only
    };

    return (
        <Controller
            name={name}
            rules={customRules}
            control={control}
            defaultValue={defaultValue}
            {...rest}
            render={({field: {onChange, value}, fieldState: {invalid, error}}) => {
                const setFieldError = (errorObj: {type: string; message: string}) => {
                    if (control) {
                        control.setError(name, {
                            type: errorObj.type,
                            message: errorObj.message,
                        });
                    }
                };

                const clearFieldErrors = () => {
                    // if (control) {
                    //     control.clearErrors(name);
                    // }
                };

                return (
                    <FileUploadContent
                        value={value as CustomFile[] | null}
                        onChange={onChange}
                        invalid={invalid}
                        error={error}
                        errorMessage={errorMessage}
                        label={label}
                        required={required}
                        showList={showList}
                        type={type}
                        previewImageSize={previewImageSize}
                        multiple={multiple}
                        accept={getAcceptTypes()}
                        maxFiles={maxFiles}
                        maxSize={maxSize}
                        sx={sx}
                        setError={setFieldError}
                        clearErrors={clearFieldErrors}
                        loading={loading}
                    />
                );
            }}
        />
    );
};

// Separate component to handle dropzone logic
const FileUploadContent: React.FC<{
    value: CustomFile[] | null;
    onChange: (value: CustomFile[] | null) => void;
    invalid: boolean;
    error: any;
    errorMessage?: string;
    label?: string | React.ReactNode;
    required?: string | ValidationRule<boolean>;
    showList?: boolean;
    type?: DropzoneType;
    previewImageSize?: number;
    multiple?: boolean;
    accept?: Record<string, string[]>;
    maxFiles?: number;
    maxSize?: number;
    sx?: any;
    setError?: (error: {type: string; message: string}) => void;
    clearErrors?: () => void;
    loading?: boolean;
}> = ({
    value,
    onChange,
    invalid,
    error,
    errorMessage,
    label,
    required,
    showList = true, // Default to true for better preview experience
    type = DropzoneType.DEFAULT,
    previewImageSize,
    multiple = true,
    accept,
    maxFiles,
    maxSize = 52428800,
    sx,
    setError,
    clearErrors,
    loading = false,
}) => {
    const files = value || null;
    const onDrop = (acceptedFiles: File[]) => {
        // Clear any existing errors when files are successfully dropped
        if (clearErrors) {
            clearErrors();
        }

        if (multiple && files) {
            onChange([...files, ...acceptedFiles]);
        } else if (multiple) {
            onChange(acceptedFiles);
        } else {
            const file = acceptedFiles[0];
            if (file) {
                onChange([file]);
            }
        }
    };
    // Add dropzone logic
    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        multiple,
        accept,
        maxFiles,
        maxSize,
        noClick: loading ? true : false,
        noKeyboard: true,
        noDrag: loading ? true : false,
        onDrop,
        onDropRejected: fileRejections => {
            // Handle rejected files and set appropriate error messages
            if (setError && fileRejections.length > 0) {
                const rejection = fileRejections[0];
                const errors = rejection.errors;

                if (errors.some(err => err.code === 'file-too-large')) {
                    setError({
                        type: 'file-too-large',
                        message: `File "${rejection.file.name}" is too large. Maximum size is ${(maxSize / 1024 / 1024).toFixed(1)}MB`,
                    });
                } else if (errors.some(err => err.code === 'file-invalid-type')) {
                    setError({
                        type: 'file-invalid-type',
                        message: `File type not allowed for "${rejection.file.name}"`,
                    });
                } else if (errors.some(err => err.code === 'too-many-files')) {
                    setError({
                        type: 'too-many-files',
                        message: `Too many files. Maximum ${maxFiles} files allowed`,
                    });
                } else {
                    // Generic error for other rejection reasons
                    setError({
                        type: 'file-rejected',
                        message: `File "${rejection.file.name}" was rejected: ${errors[0]?.message || 'Unknown error'}`,
                    });
                }
            }
        },
    });

    // Cleanup blob URLs when component unmounts or files change
    React.useEffect(() => {
        return () => {
            if (files) {
                files.forEach(file => {
                    if (file.preview && file.preview.startsWith('blob:')) {
                        URL.revokeObjectURL(file.preview);
                    }
                });
            }
        };
    }, [files]);

    return (
        <Box>
            {label && (
                <Typography variant="subtitle1" sx={{mb: 1}}>
                    {label}
                    {required && (
                        <Typography component="span" color="error.main" sx={{ml: 0.5}}>
                            *
                        </Typography>
                    )}
                </Typography>
            )}

            <Box
                sx={{
                    width: '100%',
                    ...(type === DropzoneType.STANDARD && {width: 'auto', display: 'flex'}),
                    ...sx,
                    position: 'relative', // for overlay
                }}
            >
                <Stack {...(type === DropzoneType.STANDARD && {alignItems: 'center'})}>
                    <DropzoneWrapper
                        {...getRootProps()}
                        sx={{
                            ...(type === DropzoneType.STANDARD && {p: 0, m: 1, width: 64, height: 64}),
                            ...(isDragActive && {opacity: 0.72}),
                            ...(invalid && {
                                color: 'error.main',
                                borderColor: 'error.light',
                                bgcolor: 'error.lighter',
                            }),
                            position: 'relative',
                        }}
                    >
                        <input {...getInputProps()} style={{display: 'none'}} />
                        <PlaceholderContent type={type} />
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                    </DropzoneWrapper>
                </Stack>
            </Box>

            {(invalid || errorMessage) && (
                <FormHelperText error sx={{mt: 1, mx: 1.75}}>
                    {errorMessage || error?.message}
                </FormHelperText>
            )}
        </Box>
    );
};

export default AppControlledMediaFileUpload;
